import moment from 'moment'

export const getOpsHoursAndStoreHours = (storeHour, setOpsHours, setStoreHours) => {
    const updateHours = (timeString, hours) => {
        let [hoursStr, minutesStr] = timeString.split(':')
        let hoursNum = parseInt(hoursStr)
        let minutesNum = parseInt(minutesStr)

        hoursNum += hours

        if (hoursNum < 0) {
            hoursNum += 24
        } else if (hoursNum >= 24) {
            hoursNum -= 24
        }

        let updatedTimeString = `${String(hoursNum).padStart(1, '0')}:${String(minutesNum).padStart(2, '0')}`
        return updatedTimeString
    }
    let storeStart = updateHours(storeHour.open_time, 4)
    let storeEnd = updateHours(storeHour.close_time, -2)
    setStoreHours([storeStart, storeEnd])
    let opsStart = parseInt(storeHour?.open_time?.split(':')[0])
    let opsEnd = parseInt(storeHour?.close_time?.split(':'))
    if (parseInt(opsEnd[1]) > 0) {
        opsEnd = parseInt(opsEnd[0]) + 1
    } else {
        opsEnd = parseInt(opsEnd[0])
    }
    opsEnd =
        opsEnd < opsStart || storeHour?.next_day_end
            ? parseInt(storeHour?.close_time?.split(':')[0]) + 24
            : parseInt(storeHour?.close_time?.split(':')[0])
    setOpsHours([opsStart, opsEnd])
}

export const getPdfDisplayHour = (shiftDate, dayOrDate, hour) => {
    const rawHour = hour === undefined ? undefined : parseInt(hour, 10)

    if (dayOrDate instanceof Date) {
        if (!shiftDate) {
            return dayOrDate.getHours()
        }

        const baseDate = moment(shiftDate, 'YYYY-MM-DD').startOf('day')
        const actualDate = moment(dayOrDate)
        const dayDiff = actualDate.clone().startOf('day').diff(baseDate, 'days')
        return actualDate.hour() + dayDiff * 24
    }

    if (!shiftDate || !dayOrDate) {
        return rawHour
    }

    const baseDate = moment(shiftDate, 'YYYY-MM-DD').startOf('day')
    const actualDate = moment(dayOrDate, 'YYYY-MM-DD').startOf('day').hour(rawHour || 0)
    const dayDiff = actualDate.clone().startOf('day').diff(baseDate, 'days')
    return actualDate.hour() + dayDiff * 24
}

export const getPdfStaffRequirementKey = (shiftDate, displayHour) =>
    moment(shiftDate, 'YYYY-MM-DD').startOf('day').add(displayHour, 'hours').format('D-H')

export const getPdfMissingStaffDisplayHour = (shiftDate, timeKey) => {
    if (!timeKey) {
        return undefined
    }

    const key = String(timeKey)
    if (/^\d+$/.test(key)) {
        return parseInt(key, 10)
    }

    const fullDateHour = key.match(/^(\d{4}-\d{2}-\d{2})-(\d{1,2})$/)
    if (fullDateHour) {
        return getPdfDisplayHour(shiftDate, fullDateHour[1], fullDateHour[2])
    }

    const dayHour = key.match(/^(\d{1,2})-(\d{1,2})$/)
    if (dayHour && shiftDate) {
        const baseDate = moment(shiftDate, 'YYYY-MM-DD')
        const nextDate = baseDate.clone().add(1, 'day')
        const day = parseInt(dayHour[1], 10)
        const actualDate = day === nextDate.date() ? nextDate : baseDate
        return getPdfDisplayHour(shiftDate, actualDate.format('YYYY-MM-DD'), dayHour[2])
    }

    const hour = parseInt(key.split('-').pop(), 10)
    return Number.isNaN(hour) ? undefined : hour
}

/**
 * True when a staff requirement has a usable hourly customer prediction.
 * Treats null/undefined/false (legacy fixture value) as missing.
 */
export const hasHourlyCustomerPred = staffReq => {
    const value = staffReq?.customer_preds
    return value != null && value !== false && !Number.isNaN(Number(value))
}

/**
 * True when a day-level 予定売上 is set — including an intentional 0.
 * Only null/undefined/''/NaN are treated as missing (so callers must not use `||`).
 */
export const hasDayCustomerPrediction = value =>
    value != null && value !== '' && Number.isFinite(Number(value))

/**
 * Prefer day-level customer prediction when present (including 0); otherwise ML total.
 */
export const resolveDayPredictedSales = (customerPredictedSales, predictedSales) =>
    hasDayCustomerPrediction(customerPredictedSales) ? Number(customerPredictedSales) : predictedSales

/**
 * When a day-level 予定売上 (customer prediction) exists but hourly customer_preds
 * were never distributed (e.g. activerecord-import skipped callbacks), scale ML
 * pred_sales so the hourly breakdown still composes to the day total.
 *
 * Skip only when existing customer_preds already sum to the day total. Flat
 * operating-hours fallbacks (default customer_preds=10000) must still be scaled.
 */
export const withDistributedCustomerPreds = (staffRequirements = [], customerPredictedSales) => {
    if (!hasDayCustomerPrediction(customerPredictedSales)) {
        return staffRequirements
    }
    const dayTotal = Number(customerPredictedSales)
    if (staffRequirements.length === 0) {
        return staffRequirements
    }

    const existingCustomerSum = staffRequirements.reduce((sum, sr) => {
        return hasHourlyCustomerPred(sr) ? sum + Number(sr.customer_preds) : sum
    }, 0)
    const hasAnyCustomerPreds = staffRequirements.some(hasHourlyCustomerPred)
    // Already composed to this day total — leave the curve alone.
    if (hasAnyCustomerPreds && Math.abs(existingCustomerSum - dayTotal) <= 1) {
        return staffRequirements
    }

    const totalMl = staffRequirements.reduce((sum, sr) => sum + (Number(sr.pred_sales) || 0), 0)
    if (totalMl <= 0) {
        // No ML curve to scale; still stamp zeros so hourly cells match a 0 day total.
        if (dayTotal === 0) {
            return staffRequirements.map(sr => ({ ...sr, customer_preds: 0 }))
        }
        return staffRequirements
    }

    return staffRequirements.map(sr => ({
        ...sr,
        customer_preds: ((Number(sr.pred_sales) || 0) / totalMl) * dayTotal
    }))
}

export const sumHourlyPredSales = (preds = [], hours = []) =>
    (hours || []).reduce((sum, hour) => sum + (Number(preds[hour]?.pred_sales) || 0), 0)

export const sumHourlyStaffRequired = (preds = [], hours = []) =>
    (hours || []).reduce((sum, hour) => sum + (Number(preds[hour]?.staff_required) || 0), 0)

/**
 * Convert yen to 千円 rounded to one decimal (the PDF hour-cell display unit).
 * Returns null when the value is missing / non-numeric.
 */
export const yenToSenYenOneDecimal = yen => {
    if (yen == null || yen === '' || Number.isNaN(Number(yen))) {
        return null
    }
    return Math.round(Number(yen) / 100) / 10
}

/**
 * Format a 千円 value for PDF cells.
 * Always prefer one decimal. Only apply the narrow hour-column 5-char budget
 * when `fitHourCell` is true — 合計 uses a wider cell and must keep one decimal
 * so it stays aligned with the sum of displayed hourly values.
 */
export const formatSenYenForPdf = (senYen, { fitHourCell = false } = {}) => {
    if (senYen == null || senYen === 0) {
        return '-'
    }
    const withOneDecimal = Number(senYen).toFixed(1)
    // Hour cells are 19px at fontSize 8; staff hours already render up to 5 chars.
    if (fitHourCell && withOneDecimal.length > 5) {
        return String(Math.round(Number(senYen)))
    }
    return withOneDecimal
}

/**
 * Format a yen amount as 千円 for PDF hour cells.
 * Previously truncated to whole 千円 (`String(Math.round(price)).slice(0, -3)`),
 * which made the hourly row sum diverge from 合計 after per-cell rounding.
 */
export const priceFormatter = price =>
    formatSenYenForPdf(yenToSenYenOneDecimal(price), { fitHourCell: true })

/**
 * Sum of the *displayed* hourly 千円 values (one decimal each), so 合計 matches
 * what the hour cells show rather than re-rounding the raw yen total.
 */
export const sumDisplayedHourlySenYen = (preds = [], hours = []) =>
    (hours || []).reduce((sum, hour) => {
        const senYen = yenToSenYenOneDecimal(preds[hour]?.pred_sales)
        return sum + (senYen || 0)
    }, 0)

export const buildPdfHourlyPreds = (
    staffRequirements = [],
    shiftDate,
    missingStaffHours = {},
    customerPredictedSales = null
) => {
    const preds = []
    const effectiveReqs = withDistributedCustomerPreds(staffRequirements, customerPredictedSales)
    effectiveReqs.forEach(staff => {
        const { day, hour, pred_sales, customer_preds, customer_staff_required, staff_required } = staff
        const displayHour = getPdfDisplayHour(shiftDate, day, hour)
        preds[displayHour] = {
            pred_sales: hasHourlyCustomerPred(staff) ? customer_preds : pred_sales,
            staff_required: customer_staff_required || staff_required
        }
    })

    Object.entries(missingStaffHours || {}).forEach(([time, val]) => {
        const displayHour = getPdfMissingStaffDisplayHour(shiftDate, time)
        if (displayHour === undefined) {
            return
        }
        const missing = val > 0 ? val : 0
        preds[displayHour] = {
            ...preds[displayHour],
            missing
        }
    })

    return preds
}
